import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Plus, Minus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { edadEnFecha } from "@/lib/curp";
import { docenteLabel } from "@/lib/format";

const NIVEL_LABEL = { PREESCOLAR: "Preescolar", PRIMARIA: "Primaria", SECUNDARIA: "Secundaria" };

export default function DatosAlumnos() {
  const { grupoId } = useParams();
  const { user, fechaCorteEdad } = useAuth();
  const [grupo, setGrupo] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [extraLabel, setExtraLabel] = useState("Observaciones");
  const [extraRows, setExtraRows] = useState(5); // filas extra en blanco al final

  useEffect(() => {
    (async () => {
      try {
        const [g, a] = await Promise.all([
          api.get(`/grupos/${grupoId}`),
          api.get("/alumnos", { params: { grupo_id: grupoId, include_bajas: false } }),
        ]);
        setGrupo(g.data);
        setAlumnos(a.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [grupoId]);

  const nivel = grupo?.plantel?.nivel || "";
  const cutoff = fechaCorteEdad?.(nivel) || "";

  const rows = useMemo(() => {
    const sorted = [...alumnos].sort((a, b) => {
      const A = `${a.apellido_paterno || ""} ${a.apellido_materno || ""} ${a.nombre || ""}`.trim().toUpperCase();
      const B = `${b.apellido_paterno || ""} ${b.apellido_materno || ""} ${b.nombre || ""}`.trim().toUpperCase();
      return A.localeCompare(B, "es");
    });
    return sorted.map((a, i) => ({
      n: i + 1,
      nombre: [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" "),
      genero: a.genero === "H" ? "H" : a.genero === "M" ? "M" : "",
      edad: edadEnFecha(a.fecha_nacimiento, cutoff),
    }));
  }, [alumnos, cutoff]);

  const totalH = rows.filter((r) => r.genero === "H").length;
  const totalM = rows.filter((r) => r.genero === "M").length;

  const backTo = user?.role === "docente" ? "/docente" : "/admin/grupos";

  return (
    <AppLayout>
      {/* Print-only styles */}
      <style>{`
        @page { size: Letter portrait; margin: 12mm; }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-area { padding: 0 !important; }
          aside, nav, header[data-app-header] { display: none !important; }
          .print-page { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <div className="max-w-5xl">
        {/* Toolbar (hidden when printing) */}
        <div className="no-print mb-4 flex items-center justify-between gap-2">
          <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-[#545454] hover:text-[#10312B]">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-[#545454]">
              <span>Columna extra:</span>
              <input
                type="text"
                value={extraLabel}
                onChange={(e) => setExtraLabel(e.target.value)}
                className="h-8 w-44 px-2 rounded border border-[#E5E7EB] text-xs"
                data-testid="datos-alumnos-extra-label"
                placeholder="Ej. Observaciones"
              />
            </div>
            <div className="flex items-center gap-1 text-xs text-[#545454]">
              <span>Filas extra:</span>
              <Button
                type="button" variant="outline" size="sm" className="h-8 w-8 p-0"
                data-testid="datos-alumnos-extra-minus"
                onClick={() => setExtraRows(Math.max(0, extraRows - 1))}
              >
                <Minus className="w-3.5 h-3.5" />
              </Button>
              <span className="w-6 text-center font-mono">{extraRows}</span>
              <Button
                type="button" variant="outline" size="sm" className="h-8 w-8 p-0"
                data-testid="datos-alumnos-extra-plus"
                onClick={() => setExtraRows(extraRows + 1)}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Button
              className="bg-[#235B4E] hover:bg-[#10312B] text-white"
              data-testid="datos-alumnos-print"
              onClick={() => window.print()}
            >
              <Printer className="w-4 h-4 mr-1.5" /> Imprimir / PDF
            </Button>
          </div>
        </div>

        {/* Printable page */}
        <div className="print-page print-area bg-white border border-[#E5E7EB] shadow-sm p-8">
          {/* Membrete MOCK */}
          <div className="border-b-2 border-[#10312B] pb-3 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
                  alt="Censo 911"
                  className="w-14 h-14 object-contain shrink-0"
                  crossOrigin="anonymous"
                />
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#9F2241]">Sistema Censo 911 · Tamaulipas</div>
                  <div className="text-[15px] font-bold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    DATOS DE ALUMNOS
                  </div>
                  <div className="text-xs text-[#545454]">Formato interno · Datos por grupo</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Ciclo escolar</div>
                <div className="text-sm font-semibold text-[#10312B]">{grupo?.ciclo || "—"}</div>
              </div>
            </div>
          </div>

          {/* Encabezado del grupo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
            <Info label="Plantel" value={grupo?.plantel?.nombre} />
            <Info label="CCT" value={grupo?.plantel?.cct} mono />
            <Info label="Nivel" value={NIVEL_LABEL[nivel] || nivel} />
            <Info label="Grado y grupo" value={grupo ? `${grupo.grado}° ${grupo.letra}` : "—"} />
            <Info label="Docente" value={docenteLabel(grupo?.docente || (user?.role === "docente" ? user : null))} />
            <Info label="Fecha de corte edad" value={cutoff || "—"} mono />
            <Info label="Total H / M" value={`${totalH} / ${totalM}`} />
            <Info label="Total de alumnos" value={String(rows.length)} />
          </div>

          {/* Tabla */}
          <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-[#9F2241] font-semibold">
            Datos de alumnos
          </div>
          <table className="w-full text-xs border-collapse" data-testid="datos-alumnos-table">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <Th className="w-[8%] text-center">N°</Th>
                <Th className="w-[45%] text-left">Nombre del alumno</Th>
                <Th className="w-[9%] text-center">Género</Th>
                <Th className="w-[15%] text-center">Años cumplidos<br/><span className="text-[9px] opacity-80">al {cutoff || "—"}</span></Th>
                <Th className="text-left">{extraLabel || "Columna extra"}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="border border-[#D1D5DB] py-8 text-center text-[#9CA3AF]">
                    Sin alumnos registrados en este grupo.
                  </td>
                </tr>
              ) : rows.map((r) => (
                <tr key={r.n} className="odd:bg-white even:bg-[#F9FAFB]" data-testid={`datos-alumnos-row-${r.n}`}>
                  <Td className="text-center font-mono">{r.n}</Td>
                  <Td className="uppercase">{r.nombre}</Td>
                  <Td className="text-center">{r.genero}</Td>
                  <Td className="text-center font-semibold">{r.edad !== "" ? r.edad : ""}</Td>
                  <Td className="min-h-[22px]">&nbsp;</Td>
                </tr>
              ))}
              {Array.from({ length: extraRows }).map((_, i) => (
                <tr key={`extra-${i}`} className="odd:bg-white even:bg-[#F9FAFB]">
                  <Td className="text-center font-mono text-[#9CA3AF]">{rows.length + i + 1}</Td>
                  <Td>&nbsp;</Td>
                  <Td>&nbsp;</Td>
                  <Td>&nbsp;</Td>
                  <Td>&nbsp;</Td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Firmas */}
          <div className="grid grid-cols-2 gap-8 mt-10 text-xs">
            <SignatureBlock label="Docente titular" name={docenteLabel(grupo?.docente || (user?.role === "docente" ? user : null))} />
            <SignatureBlock label="Director(a) del plantel" name="" />
          </div>

          <div className="mt-6 text-[9px] text-[#9CA3AF] text-right">
            Documento generado por el Sistema Censo 911 · {new Date().toLocaleDateString("es-MX")}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className={`text-[#10312B] font-medium ${mono ? "font-mono text-[11px]" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`border border-[#10312B] px-2 py-1.5 text-[11px] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`border border-[#D1D5DB] px-2 py-1 ${className}`}>{children}</td>;
}

function SignatureBlock({ label, name }) {
  return (
    <div className="text-center">
      <div className="border-t border-[#10312B] mt-8 pt-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
        {label}
      </div>
      <div className="text-sm font-medium text-[#10312B] mt-0.5">{name || "\u00A0"}</div>
    </div>
  );
}
